import axios from 'axios';
import * as Sentry from '@sentry/react';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

/**
 * Shared implementation for the Label / MSDS PDF upload endpoints.
 * Posts multipart/form-data with a single 'file' field. Captures
 * exceptions to Sentry so network and validation failures are visible
 * without callers having to instrument each route site.
 */
async function uploadProductPdf(
  id: string,
  field: 'label-pdf' | 'msds-pdf',
  file: File
): Promise<SprayProduct> {
  return Sentry.startSpan(
    {
      name: `spray.product.${field}.upload`,
      op: 'http.client',
      attributes: { productId: id, field, size: file.size, originalName: file.name },
    },
    async () => {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(
          `/api/v1/consumables/chemicals/${id}/${field}`,
          formData,
          { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
        );
        return res.data;
      } catch (err) {
        Sentry.captureException(err);
        throw err;
      }
    }
  );
}

async function deleteProductPdf(
  id: string,
  field: 'label-pdf' | 'msds-pdf'
): Promise<SprayProduct> {
  try {
    const res = await axios.delete(
      `/api/v1/consumables/chemicals/${id}/${field}`,
      { headers: authHeaders() }
    );
    return res.data;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

/**
 * Resolve a short-lived signed S3 URL for viewing a product PDF. The
 * stored URL on the server is an S3 key (not a browser URL), so the UI
 * requests a fresh signed URL each time the user clicks View.
 */
async function resolveProductPdfSignedUrl(
  id: string,
  field: 'label-pdf' | 'msds-pdf'
): Promise<string> {
  try {
    const res = await axios.get(
      `/api/v1/consumables/chemicals/${id}/${field}/signed-url`,
      { headers: authHeaders() }
    );
    return res.data.url;
  } catch (err) {
    Sentry.captureException(err);
    throw err;
  }
}

export type IntensityLabel = 'low' | 'medium' | 'medium/heavy' | 'heavy';

/**
 * Tracks the auto-generation lifecycle of a per-combo Spray Instruction PDF.
 *   'none'        — no instruction has ever been generated for this combo.
 *   'idle'        — a valid PDF exists; calibration inputs are unchanged.
 *   'regenerating'— an async regen job is in flight server-side.
 *   'failed'      — the last regen attempt threw an error.
 *
 * Returned by the backend on every combo GET (Task 14). Used by
 * ComboInstructionCell to decide which status chip / action to render.
 */
export type ComboRegenStatus = 'none' | 'idle' | 'regenerating' | 'failed';

export interface IntensityLevel {
  label: IntensityLabel;
  gear?: string;
  pressure?: number;
  deliveryPerMinute?: number;
  metersPerMinute?: number;
  treesPerMinute?: number;
  /** Effective volume per tree — `override ?? derived` (set server-side). */
  volumePerTree?: number;
  /** Formula-derived volume per tree, frozen at save time. */
  volumePerTreeDerived?: number;
  /** Operator-typed override (positive number) — overrides the derived value. */
  volumePerTreeOverride?: number;
  /** Effective litres per hectare — `override ?? derived` (set server-side). */
  litresPerHectare?: number;
  /** Formula-derived litres per hectare, frozen at save time. */
  litresPerHectareDerived?: number;
  /** Operator-typed override (positive number) — overrides the derived value. */
  litresPerHectareOverride?: number;
}

/**
 * Audit-history entry on a calibration. Phase 3 of
 * machine_calibration_overrides_20260427. `previousSnapshot` is the entire
 * pre-edit calibration document with `editHistory` stripped (linear append).
 */
export interface CalibrationEditHistoryEntry {
  editedAt: string;
  editedBy: string;
  previousSnapshot: Omit<SprayCalibration, 'editHistory'>;
}

export interface SprayCalibration {
  _id?: string;
  date: string;
  performedBy: string;
  signedOffBy?: string;
  attachmentKey?: string;
  /**
   * Soft-delete status. Absent on legacy records — interpreted as 'active'.
   * Set to 'deleted' by the soft-delete endpoint (Phase 5 of
   * machine_calibration_overrides_20260427).
   */
  status?: 'active' | 'deleted';
  /**
   * Audit log of edits. Appended to atomically by the PUT edit route on
   * every successful edit / restore / soft delete. Phase 3.
   */
  editHistory?: CalibrationEditHistoryEntry[];
  treeSpacing?: number;
  treesPerHectare?: number;
  intensityLevels: IntensityLevel[];
}

export interface SprayCalibrationInput {
  date: string;
  performedBy: string;
  signedOffBy?: string;
  attachmentKey?: string;
  treeSpacing?: number;
  treesPerHectare?: number;
  intensityLevels: IntensityLevel[];
}

export interface SprayMachine {
  _id: string;
  tenantId: string;
  name: string;
  tractorName?: string;
  sprayerName?: string;
  tankVolume?: number;
  engineRevs?: number;
  pto?: string;
  nozzlesConfig?: string;
  calibrations?: SprayCalibration[];
  createdAt?: string;
}

export interface SprayMachineInput {
  name: string;
  tractorName?: string;
  sprayerName?: string;
  tankVolume?: number;
  engineRevs?: number;
  pto?: string;
  nozzlesConfig?: string;
}

export interface SprayOperator {
  _id: string;
  tenantId: string;
  name: string;
  licenseNo: string;
  licenseExpiry?: string;
  createdAt?: string;
  /**
   * Optional User._id that "is" this operator for Field Mode purposes
   * (My Sessions visibility, /start authz, /complete authz). Only set when
   * an admin links the operator to a login user in the Operators page.
   * Track: spray_session_workflow_20260427 / Phase 3 / Task 7.
   */
  userId?: string | null;
}

export interface SprayOperatorInput {
  name: string;
  licenseNo: string;
  licenseExpiry?: string;
  // Pass `null` to clear an existing linkage; omit to leave it unchanged.
  userId?: string | null;
}

export interface Orchard {
  _id: string;
  orchardName: string;
  variety?: string;
  cultivar?: string;
  areaHectares?: number;
  activePetalFallDate?: string | null;
  /** Crop code (from GlobalCropType) for this orchard. Added in Phase 2 multi-crop. */
  cropType?: string | null;
}

/**
 * Pre-Harvest Interval — one of three interval formats. See
 * conductor/tracks/refine_product_catalog_20260416/spec.md §2.1.
 */
export type ProductPhiType = 'days' | 'petal_drop_80' | 'month_of_year';

export interface ProductPhi {
  type: ProductPhiType;
  value: string;
}

/**
 * Approved unit values for dosePer100LUnit. Must stay in sync with
 * backend/src/models/SprayProduct.js DOSE_PER_100L_UNITS.
 */
export type DosePer100LUnit = 'ml' | 'g' | 'L' | 'kg';

/** Maximum PDF upload size shared with the backend schema. */
export const PRODUCT_PDF_MAX_BYTES = 10 * 1024 * 1024;

export const DOSE_PER_100L_UNITS: DosePer100LUnit[] = ['ml', 'g', 'L', 'kg'];

export const PHI_TYPES: ProductPhiType[] = ['days', 'petal_drop_80', 'month_of_year'];

// ─── Product schema redesign (Phase 6) ─────────────────────────────────────
export const DOSE_UNITS         = ['ml', 'g', 'L', 'kg'] as const;
export const DOSE_DENOMINATORS  = ['100L', 'ha'] as const;
export const PHI_V2_UNITS       = ['days', 'months', 'petal_drop_80', 'month_of_year'] as const;

export type DoseUnit        = typeof DOSE_UNITS[number];
export type DoseDenominator = typeof DOSE_DENOMINATORS[number];
export type PhiV2Unit       = typeof PHI_V2_UNITS[number];

export interface Dose {
  value: number;
  valueMin?: number;
  valueMax?: number;
  unit: DoseUnit;
  denominator: DoseDenominator;
}

export interface PhiV2 {
  value: number;
  unit: PhiV2Unit;
}

export interface UsageRule {
  _id?: string;
  crops: string[];
  pests: string[];
  dose?: Dose;
  phi?: PhiV2;
  maxPerSeason?: { value: number; unit: DoseUnit };
  minIntervalDays?: number;
  maxApplicationsPerSeason?: number;
  notes?: string;
}

export interface RoundProductOverride {
  productId: string;
  targetPests: string[];
  doseOverride?: Dose;
  phiOverride?: PhiV2;
  notes?: string;
}

export interface ProductPdfFile {
  url: string;
  originalName: string;
  size: number;
}

export interface ActiveIngredient {
  name: string;
  value: number;
  unit: ActiveIngredientUnit;
}

export interface SprayProduct {
  _id: string;
  productName: string;
  lNumber: string;
  activeIngredients: ActiveIngredient[];
  category?: string;
  dosePer100L?: number;
  dosePer100LUnit?: DosePer100LUnit;
  stockUnit?: string;
  phi?: ProductPhi;
  pestTreated?: string;
  formulation?: string;
  labelPdf?: ProductPdfFile;
  msdsPdf?: ProductPdfFile;
  tenantId: string | null;
  // Phase 8 (Consumables Procurement + Xero stub): chemicals imported from
  // Xero arrive with only productName/lNumber populated and `needsReview=true`.
  // SprayProductForm shows a warn banner until the user fills in the missing
  // fields; backend then clears the flag on update.
  needsReview?: boolean;
  usageRules?: UsageRule[];
}

export interface SprayProductInput {
  productName: string;
  lNumber: string;
  activeIngredients: ActiveIngredient[];
  category?: string;
  dosePer100L?: number;
  dosePer100LUnit?: DosePer100LUnit;
  stockUnit?: string;
  phi?: ProductPhi;
  pestTreated?: string;
  formulation?: string;
  usageRules?: UsageRule[];
}

export interface InventoryTransaction {
  _id: string;
  tenantId: string;
  productId: string;
  type: 'purchase' | 'consumption' | 'adjustment' | 'reversal';
  quantity: number;
  date: string;
  referenceId?: string;
  createdAt: string;
  // Spec B enrichment (Phase 5): backend resolves the underlying consumable
  // so the UI can render a readable product label without a follow-up fetch.
  consumable?: {
    _id: string;
    productName: string;
    consumableType: 'chemical' | 'ppe' | 'fertilizer' | 'diesel';
    lNumber?: string;
  };
  /** Human-readable reference resolved by the backend (e.g. "Session SES-42"). */
  referenceLabel?: string;
  /** Supplier name on purchase rows (Spec B procurement). */
  supplier?: string;
  /** Unit string for the quantity on purchase rows. */
  quantityUnit?: string;
}

export interface ProductConsumed {
  productId: string;
  quantity: number;
  plannedQuantity?: number;
  /**
   * Unit the consumed `quantity` is expressed in — the dose unit locked in at
   * completion (e.g. 'g', 'ml'). This is the authoritative display unit for the
   * consumption row; it is NOT the product-catalog `dosePer100LUnit`, which can
   * differ (a product dosed in 'g' but catalogued in 'kg'). Mirrors the ledger
   * consumption row's `quantityUnit`.
   */
  quantityUnit?: string;
}

export interface PpeChecklistItem {
  item: string;
  checked: boolean;
}

/**
 * A SprayInstruction reference as it appears on a populated session payload.
 * Returned by GET /sessions/:id with the projection
 *   { _id, createdAt, createdBy, pdfKey, calibrationSnapshot }
 * and createdBy nested-populated with { name, username }. Used by the
 * Attached Instructions history list on SpraySessionDetail.
 *
 * Track: spray_session_enhancements_20260426 / Phase 1.6.
 */
export interface AttachedSprayInstruction {
  _id: string;
  createdAt: string;
  createdBy?: {
    _id?: string;
    name?: string;
    username?: string;
  } | string;
  pdfKey?: string;
  calibrationSnapshot?: {
    intensityLabel?: IntensityLabel;
    litresPerHectare?: number;
    [key: string]: unknown;
  };
}

/**
 * Per-(operator × machine) combo persisted under a parent SpraySession.
 *
 * Backend collection: OperatorSpraySession. One row per assignment.
 * GET /sessions/:id ships the populated array on the response so the
 * frontend can render combo-level UI without an extra round-trip.
 *
 * Only the fields exercised by the current UI are typed here; the
 * backend ships much more (weatherSnapshots, productsConsumed,
 * tankCount, ppeChecklist, auditTrail, …). Widen as later Phase 5
 * tasks reach for those fields.
 */
/**
 * Per-combo weather snapshot. Matches the backend's lightweight read shape;
 * the operator UI in Phase 6 will replace this with the richer
 * `systemReported` / `finalSelected` snapshot envelope. Only the fields the
 * read-only ComboSummaryList renders are typed here.
 */
export interface ComboWeatherSnapshot {
  // Very-legacy raw-numeric fields (only set on data that pre-dates the
  // categorical schema). The combo summary still falls back to these
  // when nothing else is present.
  temperature?: number;
  windSpeed?: number;
  precipitation?: number;
  timestamp?: string;

  // Phase 6 / Task 6.4 flat-category shape — what the new operator UI
  // persists on /start and /complete.
  temperatureCategory?: string;
  windCategory?: string;
  precipitationCategory?: string;
  overrideComment?: string;

  // Pre-Phase-6 nested categorical shape (still written by the manager UI).
  finalSelected?: {
    temperature?: string;
    wind?: string;
    precipitation?: string;
  };

  // Raw reading from WeatherHistory at the moment of capture — sourced
  // by both the new operator UI and the prior categorical shape.
  systemReported?: {
    temp?: number;
    windSpeed?: number;
    precipitation?: number;
  };
}

/**
 * One row of the PPE attestation a combo's operator ticked off at /start.
 * `checkedAt` is set server-side when present.
 */
export interface ComboPpeChecklistItem {
  item: string;
  checked: boolean;
  checkedAt?: string;
}

/**
 * One row of a combo's audit trail. Free-form `before`/`after` snapshots
 * are intentionally typed as `unknown` — the trail surfaces verbatim in
 * the UI and we don't want stale interface drift to obscure real data.
 */
export interface ComboAuditTrailEntry {
  at: string;
  by?: string;
  action: string;
  before?: unknown;
  after?: unknown;
  reason?: string;
}

export interface OperatorSpraySessionCombo {
  _id: string;
  sessionId: string;
  operatorId: string;
  machineId: string;
  /**
   * Per-combo intensity label (Phase 6b — multi_operator_spray_phase6b).
   * Replaces the deprecated `SpraySession.intensityLevel` parent field;
   * `resolveComboIntensity` falls back to the parent during the migration
   * window. Validated against the machine's latest calibration on
   * addCombo / editCombo.
   */
  intensityLevel?: IntensityLabel;
  status: 'not_started' | 'in_progress' | 'completed' | 'incomplete' | 'cancelled';
  startedAt?: string;
  endedAt?: string;
  /** Set server-side when status transitions to 'completed'. */
  completedAt?: string;
  /** User._id of the actor who flipped the combo to 'completed'. */
  completedBy?: string;
  cancelledAt?: string;
  cancelledBy?: string;
  cancelledReason?: string;
  markedIncompleteAt?: string;
  markedIncompleteBy?: string;
  markedIncompleteReason?: string;
  /** Operator-side weather captured at /start and /complete on this combo. */
  weatherSnapshots?: {
    start?: ComboWeatherSnapshot;
    end?: ComboWeatherSnapshot;
  };
  /**
   * Per-combo product consumption. Distinct from the parent session's
   * aggregated `productsConsumed`: this is the raw row the operator
   * captured on this assignment.
   */
  productsConsumed?: Array<ProductConsumed & { unit?: string }>;
  /** Number of tanks the operator mixed for this combo. */
  tankCount?: number;
  /** PPE attestation ticked off before the combo started. */
  ppeChecklist?: ComboPpeChecklistItem[];
  /** FK to a generated SprayInstruction PDF for THIS combo. */
  instructionId?: string | null;
  /**
   * Auto-generation lifecycle for this combo's PDF instruction.
   * Set server-side on every combo GET (Task 14 of spray-pdf-auto-gen track).
   * Drives the ComboInstructionCell status chip.
   */
  regenStatus?: ComboRegenStatus;
  /** Linear append of state-change events. */
  auditTrail?: ComboAuditTrailEntry[];
  /**
   * Capture mode for this combo. 'live' for real-time capture, 'back_capture'
   * for back-capture workflows. Track: spray-back-capture / Task 1.
   */
  captureMode?: 'live' | 'back_capture';
  /**
   * User._id of the actor who performed the back-capture (if captureMode
   * is 'back_capture'). Track: spray-back-capture / Task 1.
   */
  capturedBy?: string | null;
  /**
   * Flag indicating whether the back-capture session exceeded time limits.
   * Track: spray-back-capture / Task 1.
   */
  backCaptureBeyondLimit?: boolean;
}

export interface SpraySession {
  _id: string;
  tenantId: string;
  /** Populated as { _id, name } by GET /sessions/:id; raw string elsewhere. */
  programId: string | SprayProgramRef;
  /**
   * Reference to the SprayProgram round this session was generated from.
   * Required on every session created from this track onward; legacy sessions
   * (predating the track) have `roundId` undefined — that's the canonical
   * legacy-state predicate the UI uses to render a "Legacy" badge.
   */
  roundId?: string;
  /**
   * Tenant-scoped display sequence (`SES-{seq}`). Assigned only on new
   * sessions; legacy sessions have `seq` undefined.
   */
  seq?: number;
  name?: string;
  orchardIds: string[];
  date: string;
  startDate?: string;
  endDate?: string;
  wind?: string;
  rain?: boolean;
  temperature?: number;
  productsConsumed: ProductConsumed[];
  comments?: string;
  machineId?: string | SprayMachine;
  operatorIds?: Array<string | SprayOperator>;
  /**
   * Populated by GET /sessions/:id with one entry per (operator × machine)
   * combo materialised under the session. Only present on the single-
   * session GET response; raw list endpoints (GET /sessions) ship the
   * parent only and leave this undefined.
   */
  combos?: OperatorSpraySessionCombo[];
  status?: 'draft' | 'planned' | 'in_progress' | 'completed' | 'incomplete' | 'cancelled' | 'signed_off' | 'deleted';
  /**
   * Sign-off audit fields. Set server-side when the manager flips the
   * session to `signed_off` (cleared on revoke). Track: multi-operator
   * spray sessions Phase 1 + 5.
   */
  signedOffAt?: string;
  signedOffBy?: string;
  /**
   * Incomplete-flag audit fields. Set server-side when the manager marks
   * a session as 'incomplete' (cleared when the flag is cleared and the
   * status is re-derived from the combos).
   */
  markedIncompleteAt?: string;
  markedIncompleteBy?: string;
  markedIncompleteReason?: string;
  ppeChecklist?: PpeChecklistItem[];
  /**
   * Spray instruction configuration captured directly on the session so
   * generation can be a single-click action. Calibration parameters
   * (litresPerHectare, gear, etc.) are NOT stored here — only the
   * intensity label is stored, and the calibration is resolved
   * dynamically at generation time.
   *
   * Track: spray_session_enhancements_20260426 / Phase 1.1.
   */
  intensityLevel?: IntensityLabel;
  waterVolume?: number;
  mixInstructions?: string;
  /**
   * History of generated spray instruction PDFs attached to this session.
   * GET /sessions/:id populates this with AttachedSprayInstruction
   * objects (sorted newest-first); raw string IDs elsewhere.
   */
  instructionIds?: Array<string | AttachedSprayInstruction>;
  // Soft-delete audit fields (track session_delete_bulk_actions_20260426).
  // previousStatus is set on delete and cleared on restore; deletedAt and
  // deletedBy are set on delete and preserved across restore as an immutable
  // last-deletion record.
  previousStatus?: 'planned' | 'in_progress' | 'completed' | 'cancelled';
  deletedAt?: string;
  deletedBy?: string;
  /**
   * Set by the single-operator backfill path (spec §9.4) when an older
   * session was materialised into the new combo schema. ComboSummaryList
   * surfaces a small footnote on rows whose parent carries this flag so
   * managers know the audit trail predates the multi-operator change.
   */
  legacyAttribution?: 'single-operator';
  createdAt: string;
}

export interface SpraySessionInput {
  programId: string;
  /**
   * Round this session is being generated from. The server requires this
   * on every newly-created session linked to a programId. The dedicated
   * round-conversion endpoints set it automatically.
   */
  roundId?: string;
  name?: string;
  orchardIds?: string[];
  date: string;
  startDate?: string;
  endDate?: string;
  wind?: string;
  rain?: boolean;
  temperature?: number;
  productsConsumed?: ProductConsumed[];
  comments?: string;
  machineId?: string;
  operatorIds?: string[];
  status?: string;
  ppeChecklist?: PpeChecklistItem[];
  intensityLevel?: IntensityLabel;
  waterVolume?: number;
  mixInstructions?: string;
}

/**
 * Payload for the bulk-session endpoint. One session is produced per orchardId,
 * each named "[programName] - [orchardName]". Introduced by the
 * revise_spray_session_20260414 track to enforce the single-orchard invariant.
 */
export interface BulkSessionInput {
  programId: string;
  /**
   * Round-link required by the server contract (extend_spray_schema_20260430).
   * The dedicated round-conversion endpoints set this automatically; the
   * manual create form on the Sessions page sends it explicitly so the
   * server doesn't 400 on a bare programId.
   */
  roundId?: string;
  programName: string;
  orchardIds: string[];
  date: string;
  startDate?: string;
  endDate?: string;
  wind?: string;
  rain?: boolean;
  temperature?: number;
  productsConsumed?: ProductConsumed[];
  comments?: string;
  machineId?: string;
  operatorIds?: string[];
  status?: string;
  ppeChecklist?: PpeChecklistItem[];
  intensityLevel?: IntensityLabel;
  waterVolume?: number;
  mixInstructions?: string;
}

export interface SprayRound {
  _id?: string;
  /**
   * Multi-product list. Required on new rounds (validated server-side);
   * absent on legacy rounds — read-side code should prefer `productList`,
   * which the server injects from products / [productId] uniformly.
   *
   * Entries may be plain ObjectId strings (legacy documents) or full
   * RoundProductOverride subdoc objects (new documents). New writes always
   * use the subdoc shape so targetPests + overrides are persisted.
   */
  products?: Array<string | RoundProductOverride>;
  /**
   * @deprecated Legacy single-product field — kept on the type for legacy
   * read compatibility. New rounds write to `products`. The server's
   * deprecated-write validator rejects `productId` on new docs.
   */
  productId?: string;
  /**
   * Read-time normalised product list, server-injected on lean GET
   * responses. Resolves to `products` when present, else `[productId]`
   * for legacy rounds. Treat this as the canonical view at the UI layer.
   */
  productList?: string[];
  waterPerHectare?: number;
  plannedDate: string;
  name?: string;
  /**
   * Per-program display sequence (`R{seq}`). Assigned only on new rounds;
   * legacy rounds have `seq` undefined — that's the visible legacy-state
   * signal in the UI.
   */
  seq?: number;
}

export interface SprayProgram {
  _id: string;
  tenantId: string;
  name: string;
  plannedDate: string;
  status?: string;
  comments?: string;
  rounds: SprayRound[];
  sessionNames?: string[];
  /**
   * Tenant-scoped display sequence (`PGM-{seq}`). Assigned only on new
   * programs; legacy programs have `seq` undefined.
   */
  seq?: number;
  /** Crop code (from GlobalCropType) that drives per-round dose/PHI resolution for this program. */
  cropType?: string | null;
  createdAt: string;
}

/**
 * Minimal populated form of a SprayProgram — what the server returns on
 * GET /sessions/:id so the Session Detail can display the program name
 * without a second round-trip.
 */
export interface SprayProgramRef {
  _id: string;
  name: string;
}

export interface SprayProgramInput {
  name: string;
  plannedDate: string;
  comments?: string;
  rounds?: SprayRound[];
  /** Crop code forwarded to the backend for dose/PHI resolution. */
  cropType?: string | null;
}

export interface SessionFilters {
  dateFrom?: string;
  dateTo?: string;
  status?: string;
  /**
   * Hierarchical program/round filtering (extend_spray_schema_20260430
   * spec §6.2). The server enforces that `roundId` must be sent together
   * with `programId` — bare `roundId` returns 400 — and the
   * SpraySessionsPage URL contract mirrors that.
   */
  programId?: string;
  roundId?: string;
  /** ISO date string or 'today' shorthand; narrows sessions to a single day. */
  day?: string;
}

export interface CalibrationSnapshot {
  calibrationDate?: string;
  performedBy?: string;
  gear?: string;
  pressure?: number;
  treeSpacing?: number;
  treesPerHectare?: number;
  intensityLabel?: IntensityLabel;
  deliveryPerMinute?: number;
  metersPerMinute?: number;
  treesPerMinute?: number;
  volumePerTree?: number;
  litresPerHectare?: number;
}

export interface SprayInstruction {
  _id: string;
  tenantId: string;
  sessionId?: string;
  programId?: string;
  date: string;
  machineId?: string;
  intensityLevel?: IntensityLabel;
  calibrationSnapshot?: CalibrationSnapshot;
  operatorIds?: string[];
  orchardIds?: string[];
  waterVolume?: number;
  mixInstructions?: string;
  ppeChecklist?: PpeChecklistItem[];
  status: 'draft' | 'generated' | 'completed';
  pdfKey?: string;
  createdBy: string;
  createdAt: string;
}

// ─── Audit Report ─────────────────────────────────────────────────────────────

/**
 * Allowed active-ingredient unit values, mirrored from
 * backend/src/models/SprayProduct.js ACTIVE_INGREDIENT_UNITS. Keep in sync.
 */
export type ActiveIngredientUnit = 'g/L' | 'g/kg' | 'spores/ml';

export const ACTIVE_INGREDIENT_UNITS: ActiveIngredientUnit[] = ['g/L', 'g/kg', 'spores/ml'];

/** Case-insensitive unit canonicaliser (frontend mirror of backend unitCase). */
function makeCanonicaliser<T extends string>(list: readonly T[]): (raw: string) => string {
  const map = new Map<string, string>(list.map(u => [u.toLowerCase(), u]));
  return raw => {
    const trimmed = String(raw).trim();
    return map.get(trimmed.toLowerCase()) ?? trimmed;
  };
}

export const canonActiveIngredientUnit = makeCanonicaliser(ACTIVE_INGREDIENT_UNITS);
export const canonDosePer100LUnit      = makeCanonicaliser(DOSE_PER_100L_UNITS);
export const canonPhiType              = makeCanonicaliser(PHI_TYPES);

/**
 * Filter set accepted by both /audit-report/preview and /audit-report/export.
 * Dates are ISO strings (YYYY-MM-DD or full ISO). Empty arrays are treated
 * as "no filter" by the backend.
 *
 * The audit only includes spray sessions with status='completed'. The
 * `sessionIds` filter further narrows the eligible set; an empty/absent
 * value means "all completed sessions in the date range."
 */
export interface AuditReportFilters {
  startDate: string;
  endDate: string;
  orchardIds?: string[];
  sessionIds?: string[];
}

/**
 * One row per (SpraySession, productConsumed) pair, mirroring the row shape
 * the backend service produces. Fields that the Citrus Synch domain doesn't
 * track yet (e.g. PUC, technicalPermission) live in the Excel layout but
 * aren't surfaced on this row — the preview UI uses only what's listed here.
 */
export interface AuditRow {
  sessionId: string;
  sessionName?: string | null;
  sessionDate: string;
  sessionRef?: string | null;
  season?: number;

  growerName?: string | null;

  orchardId?: string | null;
  orchardName?: string | null;
  orchardFarmName?: string | null;
  orchardVariety?: string | null;
  orchardCultivar?: string | null;
  orchardAreaHectares?: number | null;

  programId?: string | null;
  programName?: string | null;

  productId?: string | null;
  productName?: string | null;
  lNumber?: string | null;
  activeIngredient?: string | null;
  activeIngredientValue?: number | null;
  productUnit?: ActiveIngredientUnit | string | null;
  productDosePer100L?: number | null;
  productDosePer100LUnit?: DosePer100LUnit | string | null;

  quantityUsed?: number | null;
  quantityUnit?: DosePer100LUnit | string | null;

  activeIngredientKg?: number | null;
  activeIngredientAmount?: number | null;
  activeIngredientUnit?: 'g' | 'spores' | null;

  gaps: string[];

  // Rule-reasoning fields (added in Task 5.1 backend schema update)
  resolvedFrom?: 'round_override' | 'product_rule' | 'manual' | 'unresolved' | null;
  matchedRules?: AuditMatchedRule[];
  doseSourceRuleId?: string | null;
  phiSourceRuleId?: string | null;
  legacyPreSnapshot?: boolean;
  appliedTargetPests?: string[];
}

export interface AuditMatchedRuleDose {
  value: number;
  unit: string;
  denominator: string;
}

export interface AuditMatchedRulePhi {
  value: number;
  unit: string;
}

export interface AuditMatchedRule {
  ruleId: string;
  crops?: string[];
  pests?: string[];
  dose?: AuditMatchedRuleDose | null;
  phi?: AuditMatchedRulePhi | null;
}

/**
 * Per-active-ingredient total. Mass-comparable products (g/L, g/kg)
 * report under unit='kg'; biologic products (spores/ml) under
 * unit='spores'. Same AI in different formulations gets two entries.
 */
export interface AuditActiveIngredientTotal {
  activeIngredient: string;
  unit: 'kg' | 'spores' | string;
  amount: number;
}

export interface AuditSummary {
  totalRows: number;
  totalGaps: number;
  gapCounts: Record<string, number>;
  activeIngredientTotals: AuditActiveIngredientTotal[];
}

export interface AuditAggregateBucketByCrop {
  variety: string | null;
  activeIngredient: string;
  unit: 'kg' | 'spores' | string;
  amount: number;
}

export interface AuditAggregateBucketByMonth {
  month: string;
  activeIngredient: string;
  unit: 'kg' | 'spores' | string;
  amount: number;
}

export interface AuditAggregateBucketByCropMonth {
  variety: string | null;
  month: string;
  activeIngredient: string;
  unit: 'kg' | 'spores' | string;
  amount: number;
  hectares: number;
  amountPerHa: number | null;
}

export interface AuditAggregates {
  kgPerCrop: AuditAggregateBucketByCrop[];
  kgPerMonth: AuditAggregateBucketByMonth[];
  kgPerHaPerMonth: AuditAggregateBucketByCropMonth[];
}

export interface AuditPreviewResponse {
  rows: AuditRow[];
  summary: AuditSummary;
  aggregates: AuditAggregates;
}

/**
 * Fields the user is allowed to override at export time as a manual stopgap
 * for a data gap. Mirrored from the backend's ALLOWED_OVERRIDE_FIELDS.
 */
export type AuditOverrideField =
  | 'activeIngredient'
  | 'activeIngredientValue'
  | 'productUnit'
  | 'productDosePer100L'
  | 'productDosePer100LUnit'
  | 'orchardAreaHectares'
  | 'orchardVariety';

export const AUDIT_OVERRIDE_FIELDS: AuditOverrideField[] = [
  'activeIngredient',
  'activeIngredientValue',
  'productUnit',
  'productDosePer100L',
  'productDosePer100LUnit',
  'orchardAreaHectares',
  'orchardVariety',
];

export interface AuditOverride {
  sessionId: string;
  productId: string;
  field: AuditOverrideField;
  value: string | number | null;
}

export const sprayService = {
  async getOrchards(): Promise<Orchard[]> {
    try {
      const res = await axios.get('/api/v1/orchards', { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getProducts(): Promise<SprayProduct[]> {
    try {
      const res = await axios.get('/api/v1/consumables/chemicals', { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  // Single-product fetch used by the route-level detail page (/spray/products/:id).
  // There is no dedicated GET /chemicals/:id endpoint; the polymorphic
  // GET /api/v1/consumables/:id returns the chemical discriminator with all
  // SprayProduct fields hydrated, which is shape-compatible.
  async getProduct(id: string): Promise<SprayProduct> {
    try {
      const res = await axios.get(`/api/v1/consumables/${id}`, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async createProduct(data: SprayProductInput): Promise<SprayProduct> {
    try {
      const res = await axios.post('/api/v1/consumables/chemicals', data, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async updateProduct(id: string, data: SprayProductInput): Promise<SprayProduct> {
    try {
      const res = await axios.put(`/api/v1/consumables/chemicals/${id}`, data, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async deleteProduct(id: string): Promise<void> {
    try {
      await axios.delete(`/api/v1/consumables/chemicals/${id}`, { headers: authHeaders() });
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async uploadProductLabelPdf(id: string, file: File): Promise<SprayProduct> {
    return uploadProductPdf(id, 'label-pdf', file);
  },

  async deleteProductLabelPdf(id: string): Promise<SprayProduct> {
    return deleteProductPdf(id, 'label-pdf');
  },

  async uploadProductMsdsPdf(id: string, file: File): Promise<SprayProduct> {
    return uploadProductPdf(id, 'msds-pdf', file);
  },

  async deleteProductMsdsPdf(id: string): Promise<SprayProduct> {
    return deleteProductPdf(id, 'msds-pdf');
  },

  async getProductLabelPdfSignedUrl(id: string): Promise<string> {
    return resolveProductPdfSignedUrl(id, 'label-pdf');
  },

  async getProductMsdsPdfSignedUrl(id: string): Promise<string> {
    return resolveProductPdfSignedUrl(id, 'msds-pdf');
  },

  async recordAdjustment(productId: string, quantity: number, date: string, referenceId?: string): Promise<InventoryTransaction> {
    try {
      const res = await axios.post('/api/v1/consumables/inventory/adjustment', { productId, quantity, date, referenceId }, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getSessions(filters?: SessionFilters): Promise<SpraySession[]> {
    try {
      const res = await axios.get('/api/v1/spray/sessions', { headers: authHeaders(), params: filters });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Field Mode "My Sessions" — fetches the caller's actionable sessions
   * (planned + in_progress, restricted to the caller's linked SprayOperator
   * server-side).
   *
   * Response shape (Phase 3 of spray_session_workflow_20260427):
   *   { sessions: SpraySession[]; hasLinkedOperator: boolean }
   *
   * The `hasLinkedOperator` flag is what lets `OperatorSessionList`
   * render the "ask your administrator to set up your operator profile"
   * empty state — distinct from "you're linked but have no sessions today".
   */
  async getMySessions(): Promise<{ sessions: SpraySession[]; hasLinkedOperator: boolean }> {
    try {
      const res = await axios.get('/api/v1/spray/sessions/my', { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getSession(id: string): Promise<{
    session: SpraySession;
    combos: OperatorSpraySessionCombo[];
    consumptionTransactions: InventoryTransaction[];
  }> {
    try {
      const res = await axios.get(`/api/v1/spray/sessions/${id}`, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async createSession(data: SpraySessionInput): Promise<SpraySession> {
    try {
      const res = await axios.post('/api/v1/spray/sessions', data, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Create one SpraySession per selected orchard for a single program. The
   * server names each session "[programName] - [orchardName]" and enforces
   * the single-orchard invariant. Returns the list of created sessions.
   */
  async createSessionsBulk(data: BulkSessionInput): Promise<SpraySession[]> {
    try {
      const res = await axios.post('/api/v1/spray/sessions/bulk', data, { headers: authHeaders() });
      return res.data.sessions;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Round-based session generation: produce one SpraySession per (round,
   * orchard) pair for the supplied round. Per
   * extend_spray_schema_20260430 spec §2:
   *
   *   - 201 → at least one session was created. `skipped` may still be
   *           non-empty when one or more orchards were already converted
   *           (mixed success).
   *   - 409 → every orchard was already converted for this round; nothing
   *           was created. The `skipped` array carries the existing
   *           session reference(s) so the UI can deep-link to them.
   *
   * Both 201 and 409 return `{ created, skipped }` shape; the caller
   * inspects `created.length` to differentiate.
   */
  async generateSessionsForRound(
    programId: string,
    roundId: string,
    body: { orchardIds: string[]; date?: string },
  ): Promise<{
    status: number;
    created: SpraySession[];
    skipped: Array<{ orchardId: string; reason: string; existingSessionId: string }>;
  }> {
    try {
      const res = await axios.post(
        `/api/v1/spray/programs/${programId}/rounds/${roundId}/sessions`,
        body,
        { headers: authHeaders(), validateStatus: (s) => s === 201 || s === 409 },
      );
      return { status: res.status, created: res.data.created || [], skipped: res.data.skipped || [] };
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Bulk round-based session generation: fans out across multiple rounds and
   * orchards. Always returns 201 (bulk is tolerant of partial work);
   * `created` may be empty when every requested pair was already converted.
   * Each `skipped` entry carries its source `roundId` so the UI can group
   * the summary toast per round.
   */
  async bulkSessionsFromRounds(
    programId: string,
    body: { roundIds: string[] | 'all'; orchardIds: string[]; date?: string },
  ): Promise<{
    created: SpraySession[];
    skipped: Array<{ roundId: string; orchardId: string; reason: string; existingSessionId: string }>;
  }> {
    try {
      const res = await axios.post(
        `/api/v1/spray/programs/${programId}/bulk-sessions`,
        body,
        { headers: authHeaders() },
      );
      return { created: res.data.created || [], skipped: res.data.skipped || [] };
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async updateSession(id: string, data: Partial<SpraySessionInput>): Promise<SpraySession> {
    try {
      const res = await axios.put(`/api/v1/spray/sessions/${id}`, data, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Add an (operator × machine) combo to an existing parent session.
   * Gated server-side by `requires('spraying:sessions')`.
   * Phase 5 / Task 5.2.
   */
  async addCombo(
    sessionId: string,
    input: { operatorId: string; machineId: string; intensityLevel?: IntensityLabel },
  ): Promise<OperatorSpraySessionCombo> {
    try {
      const res = await axios.post(
        `/api/v1/spray/sessions/${sessionId}/combos`,
        input,
        { headers: authHeaders() },
      );
      return res.data?.combo ?? res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Patch an existing combo. Today only the assigned machine is
   * mutable through this route; the operator on a combo is identity
   * (remove + add to swap operators).
   */
  async editCombo(
    comboId: string,
    patch: { machineId?: string; intensityLevel?: IntensityLabel },
  ): Promise<OperatorSpraySessionCombo> {
    try {
      const res = await axios.patch(
        `/api/v1/spray/operator-sessions/${comboId}`,
        patch,
        { headers: authHeaders() },
      );
      return res.data?.combo ?? res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async removeCombo(comboId: string): Promise<void> {
    try {
      await axios.delete(
        `/api/v1/spray/operator-sessions/${comboId}`,
        { headers: authHeaders() },
      );
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Per-combo "end early" lifecycle action. Records the partial work that
   * happened on this assignment before the operator stopped, transitioning
   * the combo to `incomplete`. Backed by
   *   POST /api/v1/spray/operator-sessions/:id/end-early
   * which is gated by `requiresOperatorOrManager`.
   * Phase 5 / Task 5.6.
   */
  async endEarlyCombo(
    comboId: string,
    payload: {
      reason: string;
      productsConsumed: Array<ProductConsumed & { unit?: string }>;
      tankCount: number;
      weatherEnd: ComboWeatherSnapshot;
    },
  ): Promise<{ combo: OperatorSpraySessionCombo; parent: SpraySession }> {
    try {
      const res = await axios.post(
        `/api/v1/spray/operator-sessions/${comboId}/end-early`,
        payload,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Per-combo "abandon" lifecycle action — for the case where an operator
   * started the combo but no product was actually sprayed. Backend requires
   * the explicit `confirmedNoProductSprayed: true` flag so the audit trail
   * captures the manager's confirmation.
   *   POST /api/v1/spray/operator-sessions/:id/abandon
   * Gated by `requiresOperatorOrManager`. Phase 5 / Task 5.6.
   */
  async abandonCombo(
    comboId: string,
    payload: { reason: string; confirmedNoProductSprayed: boolean },
  ): Promise<{ combo: OperatorSpraySessionCombo; parent: SpraySession }> {
    try {
      const res = await axios.post(
        `/api/v1/spray/operator-sessions/${comboId}/abandon`,
        payload,
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Field Mode pre-flight start — posts the PPE checklist + startedAt
   * timestamp to flip a planned session to in_progress. Backend route:
   * POST /api/v1/spray/sessions/:id/start. Throws on HTTP error so the
   * caller can surface the localised message (PPE incomplete, sanity
   * bound, etc.).
   * Track: spray_session_workflow_20260427 / Phase 3 / Task 3.
   */
  async startSession(
    id: string,
    data: {
      ppeChecklist: PpeChecklistItem[];
      startedAt: string;
    }
  ): Promise<SpraySession> {
    try {
      const res = await axios.post(
        `/api/v1/spray/sessions/${id}/start`,
        data,
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  /**
   * Operator digital sign-off — posts the Field Mode completion payload.
   * Transitions the session from 'planned' / 'in_progress' to 'completed'
   * on the backend, stamping completedAt + completedBy for the audit
   * trail. Throws on HTTP error so the caller can surface the backend's
   * localised message (see backend/src/routes/spray.js POST /sessions/:id/complete).
   */
  async completeSession(
    id: string,
    data: {
      // Phase 3 of spray_session_workflow_20260427 hoisted PPE upstream to
      // /start; the new completion form does NOT send a ppeChecklist.
      // Kept optional for legacy queued sign-offs that may still carry one.
      ppeChecklist?: PpeChecklistItem[];
      // Optional override of session.startDate (operator retroactive
      // correction). The backend treats it as optional.
      startTime?: string;
      endTime: string;
      wind: string;
      temperature: number;
      productsConsumed?: ProductConsumed[];
      /**
       * Operator-side weather snapshots for the audit trail. When present,
       * the backend persists them to session.weatherSnapshots and SKIPS
       * writing the legacy `wind` / `temperature` fields (whose deprecated
       * validators reject a save where snapshots are also being set).
       *
       * Track: spray_weather_enhancements_20260429 / Phase 5 wiring fix.
       */
      weatherSnapshots?: {
        start?: {
          timestamp?: string;
          systemReported?: { temp?: number; windSpeed?: number; precipitation?: number };
          finalSelected?: { temperature?: string; wind?: string; precipitation?: string };
        };
        end?: {
          timestamp?: string;
          systemReported?: { temp?: number; windSpeed?: number; precipitation?: number };
          finalSelected?: { temperature?: string; wind?: string; precipitation?: string };
        };
      };
    }
  ): Promise<SpraySession> {
    try {
      const res = await axios.post(
        `/api/v1/spray/sessions/${id}/complete`,
        data,
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err: any) {
      // Prefer the backend's localised error message when present so the
      // Field Mode form can surface it verbatim to the operator.
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  async deleteSession(id: string): Promise<void> {
    try {
      await axios.delete(`/api/v1/spray/sessions/${id}`, { headers: authHeaders() });
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Manager-side session lifecycle endpoints introduced in Phase 1 of the
   * multi-operator spray-session track and wired into the UI in Phase 5
   * (SessionSignoffPanel / Task 5.4). All five are gated server-side by
   * `requires('spraying:sessions')`; `revokeSessionSignoff` is further
   * restricted to Farm Administrators inside the service.
   *
   * Backend `spraySessionLifecycleService` returns `{ session }` envelopes
   * for all five; unwrap here so callers receive a flat `SpraySession`
   * (matches the `updateSession` / `getSession` shape callers already
   * expect when piping the result into `setSession(...)`).
   */
  async cancelSession(sessionId: string, reason: string): Promise<SpraySession> {
    try {
      const res = await axios.post(
        `/api/v1/spray/sessions/${sessionId}/cancel`,
        { reason },
        { headers: authHeaders() },
      );
      return res.data?.session ?? res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async markSessionIncomplete(sessionId: string, reason: string): Promise<SpraySession> {
    try {
      const res = await axios.post(
        `/api/v1/spray/sessions/${sessionId}/mark-incomplete`,
        { reason },
        { headers: authHeaders() },
      );
      return res.data?.session ?? res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async clearSessionIncomplete(sessionId: string): Promise<SpraySession> {
    try {
      const res = await axios.post(
        `/api/v1/spray/sessions/${sessionId}/clear-incomplete`,
        {},
        { headers: authHeaders() },
      );
      return res.data?.session ?? res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async signOffSession(sessionId: string): Promise<SpraySession> {
    try {
      const res = await axios.post(
        `/api/v1/spray/sessions/${sessionId}/signoff`,
        {},
        { headers: authHeaders() },
      );
      return res.data?.session ?? res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async revokeSessionSignoff(sessionId: string, reason: string): Promise<SpraySession> {
    try {
      const res = await axios.post(
        `/api/v1/spray/sessions/${sessionId}/signoff/revoke`,
        { reason },
        { headers: authHeaders() },
      );
      return res.data?.session ?? res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Bulk soft-delete spray sessions. Returns the Phase 1 summary
   * { succeeded, skipped, errors } so the UI can react to partial outcomes.
   * Backend POST /sessions/bulk-delete soft-deletes each id, captures audit
   * fields, and inserts inventory reversal-tx entries per spec section 7.
   */
  async bulkDeleteSessions(ids: string[]): Promise<{
    succeeded: number;
    skipped: number;
    errors: Array<{ id: string; reason: string }>;
  }> {
    try {
      const res = await axios.post(
        '/api/v1/spray/sessions/bulk-delete',
        { ids },
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Bulk restore soft-deleted spray sessions. Each session's status returns
   * to previousStatus, previousStatus is cleared, and the reversal-tx
   * entries are removed from the inventory ledger. Same summary shape as
   * bulkDeleteSessions.
   */
  async bulkRestoreSessions(ids: string[]): Promise<{
    succeeded: number;
    skipped: number;
    errors: Array<{ id: string; reason: string }>;
  }> {
    try {
      const res = await axios.post(
        '/api/v1/spray/sessions/bulk-restore',
        { ids },
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Restore a soft-deleted spray session. Backend POST /sessions/:id/restore
   * sets status from previousStatus, clears the reversal-tx entries from the
   * inventory ledger, and preserves deletedAt / deletedBy as the immutable
   * last-deletion record.
   */
  async restoreSession(id: string): Promise<SpraySession> {
    try {
      const res = await axios.post(`/api/v1/spray/sessions/${id}/restore`, {}, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getPrograms(filters?: { dateFrom?: string; dateTo?: string; status?: string }): Promise<SprayProgram[]> {
    try {
      const res = await axios.get('/api/v1/spray/programs', { headers: authHeaders(), params: filters });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async createProgram(data: SprayProgramInput): Promise<SprayProgram> {
    try {
      const res = await axios.post('/api/v1/spray/programs', data, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async updateProgram(id: string, data: Partial<SprayProgramInput>): Promise<SprayProgram> {
    try {
      const res = await axios.put(`/api/v1/spray/programs/${id}`, data, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async deleteProgram(id: string): Promise<void> {
    try {
      await axios.delete(`/api/v1/spray/programs/${id}`, { headers: authHeaders() });
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Bulk soft-delete spray programs via the new POST endpoint introduced
   * in Phase 1 (track session_delete_bulk_actions_20260426). Returns a
   * summary { succeeded, skipped, errors } so the UI can react to partial
   * outcomes. Prefer this over the legacy deletePrograms (DELETE
   * /spray/programs) for new callers.
   */
  async bulkDeletePrograms(ids: string[]): Promise<{
    succeeded: number;
    skipped: number;
    errors: Array<{ id: string; reason: string }>;
  }> {
    try {
      const res = await axios.post(
        '/api/v1/spray/programs/bulk-delete',
        { ids },
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Bulk restore soft-deleted spray programs. Restores each program's
   * status to its previousStatus (or 'open' for legacy entries with no
   * previousStatus). Returns the same summary shape as bulkDeletePrograms.
   */
  async bulkRestorePrograms(ids: string[]): Promise<{
    succeeded: number;
    skipped: number;
    errors: Array<{ id: string; reason: string }>;
  }> {
    try {
      const res = await axios.post(
        '/api/v1/spray/programs/bulk-restore',
        { ids },
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async deletePrograms(ids: string[]): Promise<void> {
    try {
      await axios.delete('/api/v1/spray/programs', { headers: authHeaders(), data: { ids } });
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async cloneProgram(id: string): Promise<SprayProgram> {
    try {
      const res = await axios.post(`/api/v1/spray/programs/${id}/clone`, {}, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async unlockProgram(id: string): Promise<SprayProgram> {
    try {
      const res = await axios.put(`/api/v1/spray/programs/${id}/unlock`, {}, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async restoreProgram(id: string): Promise<SprayProgram> {
    try {
      const res = await axios.put(`/api/v1/spray/programs/${id}/restore`, {}, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getMachines(): Promise<SprayMachine[]> {
    try {
      const res = await axios.get('/api/v1/spray/machines', { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async createMachine(data: SprayMachineInput): Promise<SprayMachine> {
    try {
      const res = await axios.post('/api/v1/spray/machines', data, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async updateMachine(id: string, data: SprayMachineInput): Promise<SprayMachine> {
    try {
      const res = await axios.put(`/api/v1/spray/machines/${id}`, data, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async deleteMachine(id: string): Promise<void> {
    try {
      await axios.delete(`/api/v1/spray/machines/${id}`, { headers: authHeaders() });
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async addCalibration(machineId: string, data: SprayCalibrationInput): Promise<SprayMachine> {
    try {
      const res = await axios.post(
        `/api/v1/spray/machines/${machineId}/calibrations`,
        data,
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  // Edit an existing calibration — appends a previousSnapshot to the
  // calibration's editHistory atomically server-side. Track Phase 3.
  // The optional `status` field on data (active|deleted) lets the same
  // endpoint serve the Phase 5 restore flow.
  async editCalibration(
    machineId: string,
    calibrationId: string,
    data: SprayCalibrationInput & { status?: 'active' | 'deleted' }
  ): Promise<SprayMachine> {
    try {
      const res = await axios.put(
        `/api/v1/spray/machines/${machineId}/calibrations/${calibrationId}`,
        data,
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getCalibrations(
    machineId: string,
    opts?: { includeDeleted?: boolean }
  ): Promise<SprayCalibration[]> {
    try {
      const params = opts?.includeDeleted ? { includeDeleted: 'true' } : undefined;
      const res = await axios.get(
        `/api/v1/spray/machines/${machineId}/calibrations`,
        { headers: authHeaders(), params }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  // Soft delete sets status='deleted' on the calibration sub-doc and
  // returns the updated machine. Restore is achieved via editCalibration
  // with { status: 'active' }. Track Phase 5.
  async softDeleteCalibration(
    machineId: string,
    calibrationId: string
  ): Promise<SprayMachine> {
    try {
      const res = await axios.delete(
        `/api/v1/spray/machines/${machineId}/calibrations/${calibrationId}`,
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  // Calibration PDF attachment lifecycle.
  // Track: machine_calibration_overrides_20260427 / Phase 2.

  async uploadCalibrationAttachment(
    machineId: string,
    calibrationId: string,
    file: File
  ): Promise<SprayMachine> {
    return Sentry.startSpan(
      {
        name: 'spray.calibration.attachment.upload',
        op: 'http.client',
        attributes: { machineId, calibrationId, size: file.size, originalName: file.name },
      },
      async () => {
        try {
          const formData = new FormData();
          formData.append('file', file);
          const res = await axios.post(
            `/api/v1/spray/machines/${machineId}/calibrations/${calibrationId}/attachment`,
            formData,
            { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' } }
          );
          return res.data;
        } catch (err) {
          Sentry.captureException(err);
          throw err;
        }
      }
    );
  },

  async getCalibrationAttachmentUrl(
    machineId: string,
    calibrationId: string
  ): Promise<string> {
    try {
      const res = await axios.get(
        `/api/v1/spray/machines/${machineId}/calibrations/${calibrationId}/attachment-url`,
        { headers: authHeaders() }
      );
      return res.data.url;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async deleteCalibrationAttachment(
    machineId: string,
    calibrationId: string
  ): Promise<SprayMachine> {
    try {
      const res = await axios.delete(
        `/api/v1/spray/machines/${machineId}/calibrations/${calibrationId}/attachment`,
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async getOperators(): Promise<SprayOperator[]> {
    try {
      const res = await axios.get('/api/v1/spray/operators', { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async createOperator(data: SprayOperatorInput): Promise<SprayOperator> {
    try {
      const res = await axios.post('/api/v1/spray/operators', data, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async updateOperator(id: string, data: SprayOperatorInput): Promise<SprayOperator> {
    try {
      const res = await axios.put(`/api/v1/spray/operators/${id}`, data, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  async deleteOperator(id: string): Promise<void> {
    try {
      await axios.delete(`/api/v1/spray/operators/${id}`, { headers: authHeaders() });
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Batch generate (or re-use) a Spray Instruction PDF per OperatorSpraySession
   * combo under the parent session. POSTs to the endpoint introduced in Phase 1
   * of the multi-operator spray-sessions track; expanded by Phase 3 to fan out
   * across all combos. The server reads each combo's machine + calibration
   * snapshot off the row itself, renders the PDF, uploads to S3 and stamps
   * `instructionId` on the combo.
   *
   * Returns an array of `{ operatorSessionId, instructionId }` — one entry per
   * combo. Empty-combo sessions resolve to `[]` (the server does not 404).
   *
   * Idempotency: per-combo cache hits are inherited from
   * generateInstructionForCombo — re-calling on an unchanged session returns
   * the cached instructionIds for each combo.
   */
  async generateSessionInstruction(
    sessionId: string
  ): Promise<Array<{ operatorSessionId: string; instructionId: string }>> {
    try {
      const res = await axios.post(
        `/api/v1/spray/sessions/${sessionId}/instructions`,
        {},
        { headers: authHeaders() }
      );
      return res.data;
    } catch (err: any) {
      // Surface backend validation messages verbatim so the UI can show the
      // localised reason (e.g. intensity-no-longer-on-latest-calibration).
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  /**
   * Per-combo Spray Instruction generation. POSTs to
   *   POST /api/v1/spray/operator-sessions/:id/instruction
   * which is gated by `spraying:sessions`. The endpoint differentiates
   * create-vs-cache via HTTP status:
   *   201 → a new instruction was rendered and uploaded.
   *   200 → calibration + waterVolume unchanged; the existing instruction was
   *         returned as a cache hit.
   * Both responses carry `{ instructionId, pdfUrl }`. The service preserves
   * the status code so the caller can surface a "created" vs "re-used" hint.
   *
   * Phase 5 / Task 5.5 introduces this so a per-row "Generate PDF" button can
   * be wired in a follow-up without re-touching the service layer.
   */
  async generateComboInstruction(
    comboId: string,
  ): Promise<{ status: 201 | 200; data: { instructionId: string; pdfUrl: string } }> {
    try {
      const res = await axios.post(
        `/api/v1/spray/operator-sessions/${comboId}/instruction`,
        {},
        { headers: authHeaders() },
      );
      return { status: res.status as 201 | 200, data: res.data };
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  /**
   * Force-regenerate the Spray Instruction PDF for a single combo. Calls
   *   POST /api/v1/spray/operator-sessions/:id/instruction
   * with the force-regen flag semantics introduced by Task 12 of the
   * spray-pdf-auto-gen track. Unlike generateComboInstruction (which
   * respects the idempotency cache), this always kicks off a fresh render.
   *
   * Returns `{ instructionId, pdfUrl }` on success.
   * Throws with the backend error message on 409 (combo locked / signed-off).
   */
  async regenerateComboInstruction(
    comboId: string,
  ): Promise<{ instructionId: string; pdfUrl: string }> {
    try {
      const res = await axios.post(
        `/api/v1/spray/operator-sessions/${comboId}/instruction`,
        { force: true },
        { headers: authHeaders() },
      );
      return res.data;
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  /**
   * Delete the Spray Instruction PDF attached to a combo. Calls
   *   DELETE /api/v1/spray/operator-sessions/:id/instruction
   * introduced by Task 13 of the spray-pdf-auto-gen track. Resolves
   * silently on 204. Throws on 409 (combo locked) or other errors.
   */
  async deleteComboInstruction(comboId: string): Promise<void> {
    try {
      await axios.delete(
        `/api/v1/spray/operator-sessions/${comboId}/instruction`,
        { headers: authHeaders() },
      );
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  /**
   * Remove a generated Spray Instruction from a session. POSTs to the
   * Phase 1 / Task 1.5 endpoint
   *   DELETE /api/v1/spray/sessions/:sessionId/instructions/:instructionId
   * which is a sequential, idempotent server-side delete: pulls the id from
   * the session's instructionIds, deletes the SprayInstruction record, and
   * best-effort deletes the S3 object. Successful 204 returns void.
   * Backend errors are surfaced verbatim so the modal can show the reason.
   */
  async deleteSessionInstruction(sessionId: string, instructionId: string): Promise<void> {
    try {
      await axios.delete(
        `/api/v1/spray/sessions/${sessionId}/instructions/${instructionId}`,
        { headers: authHeaders() }
      );
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  async getInstruction(id: string): Promise<SprayInstruction> {
    try {
      const res = await axios.get(`/api/v1/spray/instructions/${id}`, { headers: authHeaders() });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Resolve a short-lived signed S3 URL for an existing instruction's PDF.
   * The Attached Instructions list calls this then has the browser navigate
   * directly to the signed URL — no CORS preflight, no auth header on the
   * cross-origin S3 request, and the browser renders the PDF natively.
   *
   * Throws on 404 (no record / no pdfKey) so the UI can surface a clear
   * error rather than opening a blank tab.
   */
  async getInstructionSignedUrl(id: string): Promise<string> {
    try {
      const res = await axios.get(
        `/api/v1/spray/instructions/${id}/signed-url`,
        { headers: authHeaders() }
      );
      return res.data.url;
    } catch (err: any) {
      const backendMessage = err?.response?.data?.error;
      Sentry.captureException(err);
      if (backendMessage) {
        const wrapped = new Error(backendMessage);
        (wrapped as any).cause = err;
        throw wrapped;
      }
      throw err;
    }
  },

  async getInstructionPdfUrl(id: string, options?: { force?: boolean; lang?: string }): Promise<string> {
    try {
      const params: Record<string, string> = {};
      if (options?.force) params.force = 'true';
      if (options?.lang) params.lang = options.lang;
      const res = await axios.get(`/api/v1/spray/instructions/${id}/pdf`, {
        headers: authHeaders(),
        params,
        responseType: 'blob',
      });
      return URL.createObjectURL(res.data);
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Fetch the row-level audit-report dataset + summary + aggregates for the
   * given filter set. Backs the AuditReportDashboard preview table.
   */
  async getAuditReportPreview(filters: AuditReportFilters): Promise<AuditPreviewResponse> {
    try {
      const params: Record<string, string> = {
        startDate: filters.startDate,
        endDate:   filters.endDate,
      };
      if (filters.orchardIds && filters.orchardIds.length) {
        params.orchardIds = filters.orchardIds.join(',');
      }
      if (filters.sessionIds && filters.sessionIds.length) {
        params.sessionIds = filters.sessionIds.join(',');
      }
      const res = await axios.get('/api/v1/spray/audit-report/preview', {
        headers: authHeaders(),
        params,
      });
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },

  /**
   * Trigger an .xlsx export. The Buffer is returned to the browser as a Blob;
   * callers typically convert it to an ObjectURL and trigger a download with
   * a hidden anchor. `overrides` are applied as a manual stopgap for the
   * filtered run only (no persistence).
   */
  async exportAuditReport(
    filters: AuditReportFilters,
    overrides: AuditOverride[] = []
  ): Promise<Blob> {
    try {
      const res = await axios.post(
        '/api/v1/spray/audit-report/export',
        { filters, overrides },
        {
          headers: { ...authHeaders(), 'Content-Type': 'application/json' },
          responseType: 'blob',
        }
      );
      return res.data;
    } catch (err) {
      Sentry.captureException(err);
      throw err;
    }
  },
};
